import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback
} from "react";
import { capitalize } from "lodash";
import styles from "./styles.module.scss";
import cx from "classnames";
import PropTypes from "prop-types";
import DecimalInput from "../DecimalInput";
import {
  feetInchesToInches,
  inchesToFeetInches
} from "../../../utils/heightUnits";
import t from "../../../utils/translate";
import isOutOfBounds from "../../../utils/isOutOfBounds";
import { ScreenReaderLabel } from "@ww-digital/web-foundations-react/modules";
import FormErrorMessage from "../../FormErrorMessage";
import useEvent from "../../../utils/useEvent";
import useAppContext from "../../AppContext";
import useForceValidate from "../../../utils/useForceValidate";
import { SEQUENCE_HEIGHT_LIMITS } from "../../../utils/height";

function applyBoundaries(feetInches, max) {
  if (feetInches.feet === null && feetInches.inches === null) {
    return feetInches;
  }

  if (isOutOfBounds(feetInchesToInches(feetInches.inches, feetInches.feet))) {
    return inchesToFeetInches(max.total);
  }

  return {
    inches: feetInches.inches,
    feet: feetInches.feet
  };
}

function FeetInchesInput({
  name,
  market,
  label = {
    feet: {
      placeholder: "",
      appended: ""
    },
    inches: {
      placeholder: "",
      appended: ""
    }
  },
  mask = {
    feet: "0",
    inches: "00.0"
  },
  value,
  min,
  max,
  onChange,
  isShortUnit = false,
  labelClassName = "",
  errorClassName = "",
  fieldsetWrapperClassName = "",
  capitalizeLabel = false
}) {
  const { listen } = useEvent("SettingsValidation");
  const defaultValues = useMemo(
    () => applyBoundaries(inchesToFeetInches(value), max),
    [value, max]
  );
  const inches = useRef(defaultValues.inches);
  const feet = useRef(defaultValues.feet);
  const totalHeight = useRef(
    feetInchesToInches(defaultValues.inches, defaultValues.feet)
  );
  const [errors, setErrors] = useState({
    feet: false,
    inches: false,
    total: false
  });
  const [errorMessage, setErrorMessage] = useState(null);

  const { newQuiz, forceValidation } = useAppContext();

  const checkBounds = useCallback(() => {
    return {
      feet:
        feet.current === null ||
        isOutOfBounds(
          feet.current,
          newQuiz.isSequence || newQuiz.isClinical
            ? SEQUENCE_HEIGHT_LIMITS.min.feet
            : min.feet,
          newQuiz.isSequence || newQuiz.isClinical
            ? SEQUENCE_HEIGHT_LIMITS.max.feet
            : max.feet
        ),
      inches: isOutOfBounds(
        inches.current,
        newQuiz.isSequence || newQuiz.isClinical
          ? SEQUENCE_HEIGHT_LIMITS.min.inches
          : min.inches,
        newQuiz.isSequence || newQuiz.isClinical
          ? SEQUENCE_HEIGHT_LIMITS.max.inches
          : max.inches
      ),
      total: isOutOfBounds(
        totalHeight.current,
        newQuiz.isSequence || newQuiz.isClinical
          ? SEQUENCE_HEIGHT_LIMITS.min.total
          : min.total,
        newQuiz.isSequence || newQuiz.isClinical
          ? SEQUENCE_HEIGHT_LIMITS.max.total
          : max.total
      )
    };
  }, [min, max]);

  const { hasTriggered } = useForceValidate({
    trigger: forceValidation,
    validate: () => {
      if (!errorMessage) {
        setErrors(checkBounds());
      }
    }
  });

  useEffect(() => {
    let message = "";
    if (errors.feet) {
      if (feet.current === null) {
        message = t("FIELD_ERROR_REQUIRED");
      } else if (feet.current < SEQUENCE_HEIGHT_LIMITS.min.feet) {
        message = t("JS_HEIGHT_LIMIT_MIN");
      } else if (feet.current > SEQUENCE_HEIGHT_LIMITS.max.feet) {
        message = t("JS_HEIGHT_LIMIT_MAX");
      }
    } else if (errors.inches) {
      if (!inches.current) {
        message = t("FIELD_ERROR_REQUIRED");
      } else if (inches.current > SEQUENCE_HEIGHT_LIMITS.max.inches) {
        message = t("JS_HEIGHT_INCHES_LIMIT_MAX");
      }
    }
    setErrorMessage(message);
  }, [errors]);

  useEffect(() => {
    return listen(() => {
      setErrors(checkBounds());
    });
  }, [checkBounds, listen]);

  function onUpdate() {
    totalHeight.current = feetInchesToInches(inches.current, feet.current);
    setErrors(checkBounds());
    const isInvalid = Object.values(checkBounds()).includes(true);
    onChange(isInvalid ? null : totalHeight.current);
  }

  const handleInches = (_, inchVal) => {
    inches.current = inchVal;
    onUpdate();
  };

  const handleFeet = (_, feetVal) => {
    feet.current = feetVal;
    onUpdate();
  };

  const inchesId = `${name}-inch`;
  const hasError = errors.total || errors.inches || errors.feet;
  const renderError = newQuiz.isSequence ? hasError && hasTriggered : hasError;
  const placeholderFeet = newQuiz.isSequence
    ? t("JS_HEIGHT_FEET_PLACEHOLDER")
    : newQuiz.isClinical
      ? capitalize(label.feet.appended)
      : "";
  const placeholderInches = newQuiz.isSequence
    ? t("JS_HEIGHT_INCHES_PLACEHOLDER")
    : newQuiz.isClinical
      ? capitalize(label.inches.appended)
      : "";

  return (
    <>
      <div
        className={cx(
          styles.inchesFeetContainer,
          styles.appendedLabel,
          fieldsetWrapperClassName
        )}
      >
        <div
          className={cx(styles.feet, {
            [styles.hasValue]: feet.current && newQuiz.isClinical
          })}
        >
          <DecimalInput
            id={name}
            aria-required={true}
            market={market}
            mask={mask.feet}
            error={errors.feet || errors.total}
            defaultValue={feet.current ?? ""}
            onChange={handleFeet}
            tabIndex={1}
            autoFocus={true}
            placeholder={placeholderFeet}
          />
          <label
            className={cx(styles.tailingLabel, labelClassName)}
            htmlFor={name}
          >
            {isShortUnit
              ? label.feet.appendedShort
              : capitalizeLabel
                ? capitalize(label.feet.appended)
                : label.feet.appended}
          </label>
          {errors.feet && (
            <ScreenReaderLabel role="alert">
              {`Feet input of ${feet.current} must be between ${min.feet} and ${max.feet}`}
            </ScreenReaderLabel>
          )}
        </div>
        <div
          className={cx(styles.inches, {
            [styles.hasValue]: inches.current && newQuiz.isClinical
          })}
        >
          <DecimalInput
            id={inchesId}
            aria-required={true}
            market={market}
            mask={mask.inches}
            error={errors.inches || errors.total}
            defaultValue={inches.current ?? ""}
            onChange={handleInches}
            max={max.inches}
            tabIndex={2}
            placeholder={placeholderInches}
          />
          <label
            className={cx(styles.tailingLabel, labelClassName)}
            htmlFor={inchesId}
          >
            {isShortUnit
              ? label.inches.appendedShort
              : capitalizeLabel
                ? capitalize(label.inches.appended)
                : label.inches.appended}
          </label>
          {errors.inches && (
            <ScreenReaderLabel role="alert">
              {`Inches input of ${inches.current} must be between ${min.inches} and ${max.inches}`}
            </ScreenReaderLabel>
          )}
        </div>
      </div>
      {renderError && (
        <FormErrorMessage
          role="alert"
          data-test="height-error"
          className={errorClassName}
        >
          {(newQuiz.isSequence || newQuiz.isClinical) && errorMessage
            ? errorMessage
            : t("HEIGHT_INVALID")}
        </FormErrorMessage>
      )}
    </>
  );
}

FeetInchesInput.propTypes = {
  name: PropTypes.string.isRequired,
  market: PropTypes.string,
  min: PropTypes.shape({
    feet: PropTypes.number,
    inches: PropTypes.number
  }),
  max: PropTypes.shape({
    feet: PropTypes.number,
    inches: PropTypes.number
  }),
  onChange: PropTypes.func.isRequired
};

export default FeetInchesInput;
